(() => {
  const STORAGE_KEY = 'fwd:recently-viewed-products:v1';
  const MAX_STORED_PRODUCTS = 24;
  const MAX_RENDERED_PRODUCTS = 4;
  const PRODUCT_HANDLE_PATTERN = /^[^\s/?#]+$/u;
  const searchCardCache = new Map();

  function readHistory() {
    try {
      const history = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(history)) return [];
      return Array.from(new Set(
        history.filter((handle) => typeof handle === 'string' && PRODUCT_HANDLE_PATTERN.test(handle))
      ));
    } catch (error) {
      return [];
    }
  }

  function writeHistory(history) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, MAX_STORED_PRODUCTS)));
    } catch (error) {
      // Browsing should continue normally when storage is unavailable.
    }
  }

  function productCardUrl(handle, rootUrl, view) {
    const localizedRootUrl = (rootUrl || '/').replace(/\/?$/, '/');
    const url = new URL(`${localizedRootUrl}products/${encodeURIComponent(handle)}`, window.location.origin);
    url.searchParams.set('view', view);
    return url.toString();
  }

  async function fetchSearchCard(handle, rootUrl) {
    const cacheKey = `${rootUrl || '/'}:${handle}`;
    if (searchCardCache.has(cacheKey)) return searchCardCache.get(cacheKey);

    const request = fetch(productCardUrl(handle, rootUrl, 'recently-viewed-search-card'), {
      credentials: 'same-origin',
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Recently viewed search card failed: ${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = page.querySelector('[data-fwd-recently-viewed-search-card]');
      if (!card) throw new Error('Recently viewed search card markup unavailable');
      return card.innerHTML;
    }).catch((error) => {
      searchCardCache.delete(cacheKey);
      throw error;
    });

    searchCardCache.set(cacheKey, request);
    return request;
  }

  window.FWDRecentlyViewed = {
    fetchSearchCard,
    readHistory,
  };

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      return this.renderRecentlyViewedProducts();
    }

    readHistory() {
      return readHistory();
    }

    writeHistory(history) {
      writeHistory(history);
    }

    productCardUrl(handle) {
      return productCardUrl(handle, this.dataset.rootUrl, 'recently-viewed-card');
    }

    async fetchProductCard(handle) {
      const response = await fetch(this.productCardUrl(handle), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Recently viewed product failed: ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = page.querySelector('[data-fwd-recently-viewed-card]');
      if (!card) throw new Error('Recently viewed product card markup unavailable');
      return card.innerHTML;
    }

    async renderRecentlyViewedProducts() {
      const currentHandle = this.dataset.productHandle;
      if (!currentHandle || !PRODUCT_HANDLE_PATTERN.test(currentHandle)) return;

      const history = this.readHistory();
      const uniqueHistory = Array.from(new Set(history.filter((handle) => handle !== currentHandle)));
      const requestedLimit = Number.parseInt(this.dataset.limit, 10) || MAX_RENDERED_PRODUCTS;
      const limit = Math.max(1, Math.min(requestedLimit, MAX_RENDERED_PRODUCTS));
      const handlesToRender = uniqueHistory.slice(0, limit);

      this.writeHistory([currentHandle, ...uniqueHistory]);
      if (!handlesToRender.length) return;

      const grid = this.querySelector('[data-recently-viewed-grid]');
      if (!grid) return;

      const cards = await Promise.allSettled(handlesToRender.map((handle) => this.fetchProductCard(handle)));
      cards.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        const item = document.createElement('li');
        item.className = 'grid__item';
        item.innerHTML = result.value;
        grid.appendChild(item);
      });

      if (!grid.children.length) return;

      this.hidden = false;
      const status = this.querySelector('[data-recently-viewed-status]');
      if (status) status.textContent = `최근 조회 상품 ${grid.children.length}개를 불러왔습니다.`;
      document.dispatchEvent(new CustomEvent('fwd:product-cards-rendered', { detail: { container: this } }));
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
